import { eventListeners } from '@popperjs/core';
import {
  type AnchorHTMLAttributes,
  type HTMLAttributes,
  useCallback,
  useId,
  type SetStateAction,
  type Dispatch,
  type DOMAttributes,
} from 'react';
import { usePopper } from 'react-popper';

import type {
  _AriaBaseButtonProps as AriaBaseButtonProps,
  _AriaButtonProps as AriaButtonProps,
  _ButtonAria as ButtonAria,
} from '@dynatrace/strato-components/buttons';
import {
  _getFirstFocusableChild,
  _getLastFocusableChild,
  _mergeProps,
} from '@dynatrace/strato-components/core';

import { useDialog } from './useDialog.js';
import { useKeyboardInteractionOutside } from './useKeyboardInteractionOutside.js';
import {
  OverlayTriggerState,
  useOverlay,
  useOverlayTriggerState,
} from './useOverlay.js';
import { useStateRef } from './useStateRef.js';
import { usePress } from '../../buttons/button/hooks/usePress.js';
import {
  Elevation,
  useOverlayStacking,
} from '../../core/hooks/useOverlayStacking.js';
import type { FormControlRef } from '../../forms/shared-types.js';
import { isFormControlRef } from '../../forms/shared-types.js';
import {
  observeAnimationDone,
  observeOverlaySize,
  overlayHeightStrategyHandling,
  overlayWidthStrategyHandling,
} from '../utils/popper-modifiers.js';

//#region Typings
/**
 * @public
 */
export type OverlayPlacement =
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'right-start'
  | 'right-end'
  | 'left-start'
  | 'left-end';

/**
 * @public
 */
export type OverlaySizeStrategyRange = { min?: number; max?: number };
/**
 * @public
 */
export type WidthStrategy = 'content' | 'trigger' | OverlaySizeStrategyRange;
/**
 * @public
 */
export type HeightStrategy =
  | 'content'
  | 'maxViewport'
  | OverlaySizeStrategyRange;

/**
 * @public
 */
export interface UseOverlayWithTriggerUncontrolledProps {
  /**
   * Whether the overlay is open by default.
   * @defaultValue false
   */
  defaultOpen?: boolean;
  /**
   * Determines whether or not the overlay is open in a controlled scenario.
   */
  open?: never;
}

/**
 * @public
 */
export interface UseOverlayWithTriggerControlledProps {
  /**
   * Whether the overlay is open by default.
   */
  defaultOpen?: never;
  /**
   * Determines whether or not the overlay is open in a controlled scenario.
   */
  open: boolean;
}

/**
 * @public
 */
export interface UseOverlayWithTriggerBaseProps {
  /**
   * Placement of the overlay
   * @defaultValue 'bottom'
   */
  placement?: OverlayPlacement;
  /**
   * Alternative positions that will be assumed if the original placement will not fit
   * @defaultValue ['top']
   */
  placementFallbacks?: OverlayPlacement[];
  /**
   * Offset between overlay and target element
   * @defaultValue 0
   */
  offset?: number;
  /**
   * Offset along the cross axis between the overlay and target element
   * @defaultValue 0
   */
  crossOffset?: number;
  /**
   * An additional z-index elevation value to add to the one derived from the stacking order
   */
  additionalElevation?: Elevation;
  /**
   * Whether the trigger is disabled or not
   * @defaultValue false
   */
  disabled?: boolean;
  /**
   * Defines which strategy to use for setting the overlay width
   * @defaultValue 'content'
   */
  widthStrategy?: WidthStrategy;
  /**
   * Defines which strategy to use for setting the overlay height
   * @defaultValue 'content'
   */
  heightStrategy?: HeightStrategy;
  /**
   * Whether the overlay should trap the focus.
   * @defaultValue false
   */
  trapFocus?: boolean;
  /**
   * Callback fired when the open attribute changes.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Whether the overlay should close on blur.
   * @defaultValue false
   */
  shouldCloseOnBlur?: boolean;
  /**
   * Id used for the accessible label of the overlay
   */
  'aria-labelledby'?: string;
}

/**
 * @public
 */
export type UseOverlayWithTriggerProps = UseOverlayWithTriggerBaseProps &
  (
    | UseOverlayWithTriggerUncontrolledProps
    | UseOverlayWithTriggerControlledProps
  );

/**
 * @public
 */
export interface OverlayRefs {
  /**
   * Use this ref and attach it to the trigger element.
   * State handling of opening and closing will be automatically attached
   * to the trigger.
   */
  triggerRef: Dispatch<SetStateAction<HTMLElement | FormControlRef | null>>;
  /**
   * Use this ref to attach it to the overlaying element. This is used
   * to correctly position the overlay on the screen.
   */
  overlayRef: Dispatch<SetStateAction<HTMLElement | null>>;
  /**
   * Use this ref in order to give the overlay its anchoring element.
   * The overlaying element will attach to the target element by following
   * the provided placement and placementFallback strategies.
   * If the triggerRef and targetRef should be on the same element, consider
   * using the `useMergeRefs` from the package core to combine target and trigger
   * refs.
   */
  targetRef: Dispatch<SetStateAction<HTMLElement | FormControlRef | null>>;
}

/**
 * Type used for defining the 'data-ismodal' property, as well as modal accessibility and styling properties.
 * @public
 */
export interface ModalAriaProps extends HTMLAttributes<HTMLElement> {
  /** Data attribute marks the dom node as a modal for the aria-modal-polyfill. */
  'data-ismodal'?: boolean;
}

/**
 * @public
 */
export interface OverlayProps {
  /**
   * Properties for connecting the overlay to the overlay trigger.
   * Includes styles and accessibility attributes.
   */
  overlayProps: ModalAriaProps & Pick<UseOverlayWithTriggerProps, 'trapFocus'>;
  /**
   * Overlay state, which contains information about the open/closed state of the overlay,
   * as well as opening/closing/toggling methods.
   */
  overlayState: OverlayTriggerState;
  /**
   * Props for the OverlayContainer component, used in order to ensure correct
   * zIndex stacking.
   */
  overlayContainerProps: Record<string, unknown>;
}

/**
 * @deprecated Will be removed. Use `OverlayWithTriggerReturnType` to access `overlayTriggerProps` instead. `hasError` was never set.
 * @public
 */
export interface OverlayTriggerProps {
  /** Properties for the behavior and accessibility of the overlay trigger. */
  overlayTriggerProps: ButtonAria<AnchorHTMLAttributes<HTMLElement>> &
    AriaButtonProps;
  /** Whether or not the trigger is used within a component in an error state. */
  hasError?: boolean;
}

/**
 * Properties for spreading onto the trigger and overlay container, handling the
 * behavior and accessibility of the trigger and overlay.
 * @deprecated Will be removed. Use `TriggerProps` instead
 * @public
 */
export type OverlayWithTriggerProps = OverlayRefs &
  OverlayProps &
  OverlayWithTriggerReturnType['overlayTriggerProps'];

/**
 * Type reflecting the properties for the behavior and accessibility of the overlay trigger.
 * @public
 */
export type TriggerProps = Pick<
  AriaBaseButtonProps,
  'aria-haspopup' | 'aria-expanded' | 'aria-controls' | 'type'
> &
  Pick<
    DOMAttributes<Element & HTMLOrSVGElement>,
    | 'onKeyDown'
    | 'onKeyUp'
    | 'onClick'
    | 'onMouseDown'
    | 'onMouseUp'
    | 'onPointerDown'
    | 'onPointerUp'
    | 'onDragStart'
    | 'onMouseEnter'
    | 'onMouseLeave'
    | 'onTouchStart'
    | 'onTouchEnd'
    | 'onTouchMove'
    | 'onTouchCancel'
  > & {
    /** Whether the trigger is disabled. */
    disabled?: boolean;
  };

/**
 * Properties for spreading onto the trigger and overlay container, handling the
 * behavior and accessibility of the trigger and overlay.
 * @public
 */
export type OverlayWithTriggerReturnType = OverlayRefs &
  OverlayProps & {
    /** Properties for the behavior and accessibility of the overlay trigger. */
    overlayTriggerProps: TriggerProps;
  };
//#endregion Typings

/**
 * Returns the value for 'aria-haspopup' depending on the type.
 * @internal
 */
function getAriaHasPopup(
  type: string,
): boolean | 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid' | undefined {
  // Aria 1.1 supports multiple values for aria-haspopup other than just menus.
  // https://www.w3.org/TR/wai-aria-1.1/#aria-haspopup
  // However, we only add it for menus for now because screen readers often
  // announce it as a menu even for other values.
  if (type === 'menu') {
    return true;
  } else if (type === 'listbox') {
    return 'listbox';
  }
  return undefined;
}

/**
 * Custom hook used for overlays with trigger.
 * @public
 */
export function useOverlayWithTrigger(
  props: UseOverlayWithTriggerProps = {},
): OverlayWithTriggerReturnType {
  const {
    placement = 'bottom',
    placementFallbacks = ['top'],
    offset = 0,
    crossOffset = 0,
    additionalElevation,
    disabled = false,
    widthStrategy = 'content',
    heightStrategy = 'content',
    trapFocus = false,
    defaultOpen = false,
    open,
    onOpenChange,
    shouldCloseOnBlur = false,
    'aria-labelledby': ariaLabelledBy,
  } = props;

  // Create the refs needed for the overlay
  const [targetElement, setTargetElement] = useStateRef<HTMLElement>();
  const [overlayElement, setOverlayElement, overlayRef] =
    useStateRef<HTMLElement>();
  const [, setTriggerElement, triggerRef] = useStateRef<HTMLElement>();

  const overlayContainerProps = useOverlayStacking(
    undefined,
    additionalElevation,
  );

  // Create state for handling opening/closing of the calendar overlay
  const overlayState = useOverlayTriggerState({
    defaultOpen,
    isOpen: open,
    onOpenChange,
  });
  const { isOpen, close, toggle } = overlayState;

  const overlayId = useId();

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Tab' && isOpen) {
        const overlay = document.getElementById(`${overlayId}`);

        if (overlay) {
          const firstFocusableChild = _getFirstFocusableChild(overlay);
          const lastFocusableChild = _getLastFocusableChild(overlay);

          // No focusable children.
          if (!firstFocusableChild) {
            e.preventDefault();
            close();
            return;
          }

          const isFirstFocusable = firstFocusableChild === e.target;
          const isLastFocusable = lastFocusableChild === e.target;

          if (
            !trapFocus &&
            ((e.shiftKey && isFirstFocusable) ||
              (!e.shiftKey && isLastFocusable))
          ) {
            e.preventDefault();
            close();
          }
        }
      }
    },
    [close, isOpen, overlayId, trapFocus],
  );

  // Initialize the trigger to get the correct aria props and connections
  // between the trigger and the overlay.
  const triggerProps = {
    'aria-haspopup': getAriaHasPopup('dialog'),
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? overlayId : undefined,
    onKeyDown,
  };

  // usePress ensures that focus management is handled correctly across all
  // browsers. Focus is restored to the button once the popover closes.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { pressProps } = usePress({
    onPress: toggle,
    isDisabled: disabled,
  });

  const { styles: positioningStyles, attributes: positioningAttributes } =
    usePopper(targetElement, overlayElement, {
      placement: placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [crossOffset, offset],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [placement, ...placementFallbacks],
          },
        },
        overlayWidthStrategyHandling,
        {
          name: 'overlayWidthStrategyHandling',
          enabled: widthStrategy !== 'content',
          options: { widthStrategy },
        },
        overlayHeightStrategyHandling,
        {
          name: 'overlayHeightStrategyHandling',
          enabled: heightStrategy !== 'content',
          options: { heightStrategy },
        },
        // TODO: Deprecation is only happening to the public export, the internal function will still be available
        // can be removed with APPDEV-12712
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        observeOverlaySize,
        observeAnimationDone,
        eventListeners,
      ],
    });

  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  const { overlayProps } = useOverlay(
    {
      isOpen,
      isDismissible: true,
      shouldCloseOnInteractOutside: (element) => {
        const triggerElement = isFormControlRef(triggerRef)
          ? triggerRef.element
          : triggerRef;

        // If there is no trigger element, skip trigger hit check
        if (triggerElement === null) {
          return true;
        }

        // Prevent the dismiss when clicking on the trigger itself.
        // This enables the trigger to handle the toggling, instead of keeping
        // it inside an endless loop.
        return (
          element !== triggerRef.current &&
          triggerRef.current?.contains(element) === false
        );
      },
      onClose: () => {
        close();
      },
      shouldCloseOnBlur,
    },
    overlayRef,
  );

  // Get props for the dialog and its title
  const { dialogProps } = useDialog({ 'aria-labelledby': ariaLabelledBy });

  useKeyboardInteractionOutside({
    ref: overlayRef,
    isDisabled: !isOpen,
    onInteractOutside: (event) => {
      if ((event as unknown as KeyboardEvent).key === 'Escape') {
        close();
      }
    },
  });

  // A passed ref could be a regular DOM ref or our newly introduced FormControlRef imperative handler
  const updateTriggerElement = useCallback(
    (triggerRef: SetStateAction<HTMLElement | FormControlRef | null>) => {
      const newTriggerElement = isFormControlRef(triggerRef)
        ? triggerRef.element
        : (triggerRef as HTMLElement | null);
      setTriggerElement(newTriggerElement);
    },
    [setTriggerElement],
  );

  // A passed ref could be a regular DOM ref or our newly introduced FormControlRef imperative handler
  const updateTargetElement = useCallback(
    (targetRef: SetStateAction<HTMLElement | FormControlRef | null>) => {
      const newTargetElement = isFormControlRef(targetRef)
        ? targetRef.element
        : (targetRef as HTMLElement | null);
      setTargetElement(newTargetElement);
    },
    [setTargetElement],
  );

  return {
    triggerRef: updateTriggerElement,
    targetRef: updateTargetElement,
    overlayRef: setOverlayElement,
    overlayProps: {
      ..._mergeProps(
        overlayProps,
        dialogProps,
        // we also need to add the onKeyDown handler in here for closing overlays with interactive content when tabbing and shouldCloseOnBlur is false (default)
        // if shouldCloseOnBlur is set to true, the focus handlers from the returned useOverlay overlayProps take care of the closing
        // if there's no interactive content, the trigger keeps the focus, so the onKeyDown event from the triggerProps takes care of closing
        { onKeyDown: shouldCloseOnBlur ? undefined : onKeyDown },
        { style: positioningStyles.popper, id: overlayId, trapFocus },
        positioningAttributes.popper,
      ),
    },
    overlayContainerProps,
    overlayState,
    overlayTriggerProps: _mergeProps(pressProps, triggerProps, {
      type: 'button' as AriaBaseButtonProps['type'],
      disabled,
    }),
  };
}
